import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

const SECTION_LABELS = [
  ["contact", "get a quote"],
  ["careers", "join the fleet"],
  ["pricing", "engagement"],
  ["process", "how it works"],
  ["work", "our work"],
  ["network", "live network"],
  ["brokerage", "brokerage"],
  ["services", "capabilities"],
  ["about", "who we are"],
  ["home", "scroll and explore"],
];

export default function CustomCursor() {
  const [enabled, setEnabled] = useState(false);
  const [label, setLabel] = useState("scroll and explore");
  const activeRef = useRef(false);
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const lx = useSpring(x, { stiffness: 300, damping: 30, mass: 0.5 });
  const ly = useSpring(y, { stiffness: 300, damping: 30, mass: 0.5 });

  useEffect(() => {
    const desktop = () => window.innerWidth >= 768;
    if (!desktop()) return;
    setEnabled(true);

    const onResize = () => setEnabled(desktop());
    const onTouch = () => setEnabled(false);

    const sectionLabel = () => {
      const mid = window.innerHeight / 2;
      for (const [id, text] of SECTION_LABELS) {
        const el = document.getElementById(id);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.top <= mid && r.bottom >= mid) return text;
      }
      return "scroll and explore";
    };

    const move = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
      const el = e.target?.closest?.("a, button, [role='button'], input, textarea, select, [data-cursor]");
      if (el) {
        activeRef.current = true;
        const custom = el.getAttribute?.("data-cursor");
        const isField = ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName);
        setLabel(custom || (isField ? "type" : "click"));
      } else {
        activeRef.current = false;
        setLabel(sectionLabel());
      }
    };
    const onScroll = () => { if (!activeRef.current) setLabel(sectionLabel()); };

    window.addEventListener("mousemove", move);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    window.addEventListener("touchstart", onTouch, { once: true, passive: true });
    setLabel(sectionLabel());
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("touchstart", onTouch);
    };
  }, [x, y]);

  if (!enabled) return null;

  return (
    <div data-testid="custom-cursor" className="pointer-events-none fixed inset-0 z-[9999] hidden md:block">
      <motion.div style={{ x: lx, y: ly }} className="absolute top-0 left-0">
        <motion.span
          data-testid="cursor-label"
          key={label}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="block ml-5 mt-4 font-mono-x text-[10px] tracking-[0.25em] uppercase text-primary bg-background/90 backdrop-blur-md border border-primary/50 rounded-full px-3 py-1.5 whitespace-nowrap shadow-xl"
        >
          {label}
        </motion.span>
      </motion.div>
    </div>
  );
}
